import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { getPasswordStrength, PASSWORD_REQUIREMENTS } from '../utils/passwordValidation';

/**
 * PasswordRequirements Component
 * Displays password requirements and validation feedback
 *
 * @param {object} props
 * @param {string} props.password - Current password value
 * @param {boolean} props.showStrength - Show strength meter (default: true)
 * @param {boolean} props.showRequirements - Show requirements list (default: true)
 */
const PasswordRequirements = ({ password = '', showStrength = true, showRequirements = true }) => {
    const { strength } = getPasswordStrength(password);

    // Define strength colors and labels
    const strengthConfig = {
        none: { color: 'grey', label: 'No password', progress: 0 },
        weak: { color: 'error', label: 'Weak', progress: 25 },
        medium: { color: 'warning', label: 'Medium', progress: 50 },
        good: { color: 'info', label: 'Good', progress: 75 },
        strong: { color: 'success', label: 'Strong', progress: 100 }
    };

    const config = strengthConfig[strength] || strengthConfig.none;

    // Check individual requirements
    const requirements = [
        {
            label: `Minimum ${PASSWORD_REQUIREMENTS.minLength} characters`,
            met: password && password.length >= PASSWORD_REQUIREMENTS.minLength
        },
        {
            label: 'At least 1 uppercase letter (A-Z)',
            met: /[A-Z]/.test(password)
        },
        {
            label: 'At least 1 lowercase letter (a-z)',
            met: /[a-z]/.test(password)
        },
        {
            label: 'At least 1 number (0-9)',
            met: /[0-9]/.test(password)
        },
        {
            label: `At least 1 special character (${PASSWORD_REQUIREMENTS.specialChars})`,
            met: new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)
        }
    ];

    return (
        <Box sx={{ mt: 1, mb: 2 }}>
            {/* Password Strength Meter */}
            {showStrength && password && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Password Strength
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: `${config.color}.main`
                            }}
                        >
                            {config.label}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={config.progress}
                        color={config.color}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'grey.200'
                        }}
                    />
                </Box>
            )}

            {/* Requirements List */}
            {showRequirements && (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        Password Requirements:
                    </Typography>
                    {requirements.map((req, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.5
                            }}
                        >
                            {req.met ? (
                                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                                <Cancel sx={{ fontSize: 16, color: password ? 'error.main' : 'grey.400' }} />
                            )}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: req.met ? 'success.main' : (password ? 'error.main' : 'text.secondary')
                                }}
                            >
                                {req.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default PasswordRequirements;
